import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MusicPlayer } from './MusicPlayer';
import { resolveTheme, type MusicTheme } from './manifest';
import { initAudioUnlock } from './AudioUnlockService';
import { onLocationChange } from './locationChange';
import { onSpeechEvent } from '../speechService';
import { MUSIC_DEFAULT_VOLUME, getMusicIntent, setMusicIntent } from '@learnwithai/ui-shared';

// Read prefs directly from localStorage to avoid a circular import with
// PreferencesPage (which also imports from this module).
const PREFS_KEY = 'learnwithai_child_prefs';
// SINGLE authority for the default: ui-shared/musicLevels. Per-app literals
// (0.12/0.15/0.18) were a tester-reported volume-drift class — never local.
const DEFAULT_MUSIC_VOLUME = MUSIC_DEFAULT_VOLUME;

function readMusicPrefs(): {
  enabled: boolean;
  volume: number;
  theme: MusicTheme;
} {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Record<string, unknown>;
      return {
        enabled: p.backgroundMusicEnabled !== false,
        volume: typeof p.backgroundMusicVolume === 'number' ? p.backgroundMusicVolume : DEFAULT_MUSIC_VOLUME,
        theme: resolveTheme(typeof p.learningStyle === 'string' ? p.learningStyle : 'fun'),
      };
    }
  } catch { /* ignore */ }
  return { enabled: true, volume: DEFAULT_MUSIC_VOLUME, theme: 'main' };
}

// Persist a music pref change back into the shared child prefs so the on/off
// toggle and volume survive a relaunch (and aren't reverted by a prefchange
// re-read). Merges into the existing object — never clobbers other prefs.
function persistMusicPrefs(patch: { backgroundMusicEnabled?: boolean; backgroundMusicVolume?: number }): void {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...obj, ...patch }));
  } catch { /* ignore */ }
}

interface BackgroundMusicContextValue {
  enabled: boolean;
  volume: number;
  setEnabled: (v: boolean) => void;
  setVolume: (v: number) => void;
  setTheme: (theme: MusicTheme) => void;
}

const BackgroundMusicContext = createContext<BackgroundMusicContextValue>({
  enabled: true,
  volume: DEFAULT_MUSIC_VOLUME,
  setEnabled: () => {},
  setVolume: () => {},
  setTheme: () => {},
});

export function useBackgroundMusic(): BackgroundMusicContextValue {
  return useContext(BackgroundMusicContext);
}

export function BackgroundMusicProvider({ children }: { children: React.ReactNode }) {
  const initial = readMusicPrefs();
  const [enabled, setEnabledState] = useState(initial.enabled);
  const [volume, setVolumeState] = useState(initial.volume);
  const playerRef = useRef<MusicPlayer | null>(null);

  useEffect(() => {
    // REAL-PLAY confirmation → persist the durable "music intent" flag. Only set
    // it when playback ACTUALLY started (the 'playing' event), so a blocked/failed
    // autoplay never records a false intent. This is the signal that survives the
    // cross-app landing→lesson navigation and a page refresh (both boot a FRESH,
    // autoplay-blocked provider) so the FIRST gesture on the new page can resume.
    const player = new MusicPlayer(() => setMusicIntent(true));
    playerRef.current = player;

    // First-gesture start: the mount-time autoplay below is blocked until a user
    // gesture. Arm the unlock so the FIRST real interaction retries the paused
    // bed (targets the CURRENT player so a StrictMode remount can't resurrect a
    // disposed one). This is what makes music autostart on app open (tester B2).
    //
    // CROSS-APP / REFRESH RESUME (tester B2, 2026-08-31): landing (web-app) and
    // lessons (web-child) are SEPARATE SPAs/ECS services — a landing→lesson nav is
    // a hard cross-app load that unmounts one provider and boots the other fresh,
    // autoplay-blocked; a refresh is the same. When the persisted intent says music
    // WAS sounding and the user hasn't disabled it, the unlock stays armed so the
    // first tap in the new document resumes ensurePlaying() (in a lesson that first
    // tap happens almost immediately). We never auto-start without a gesture — the
    // browser autoplay policy forbids it; this only makes the first gesture resume.
    const unsubUnlock = initAudioUnlock(() => playerRef.current?.ensurePlaying());
    // Touch the intent so a resume-eligible cold load is observable (and the read
    // path is exercised even where the mount-time autoplay is blocked). The unlock
    // above already handles the actual first-gesture resume for both the fresh and
    // the intended-playing case; keeping this read explicit documents the contract.
    void getMusicIntent();

    const prefs = readMusicPrefs();
    player.setVolume(prefs.volume);
    player.setMuted(!prefs.enabled);
    player.setTheme(prefs.theme);

    // Re-assert the bed on EVERY route change. The provider is a persistent
    // singleton mounted OUTSIDE the router, and most surfaces never call
    // ensurePlaying — so after any transient pause the bed stayed silent there
    // (tester B11: "music stops when I navigate"). One subscription keeps it
    // sounding on every screen without each page opting in. Idempotent + a no-op
    // while playing / muted / blocked, so it respects the parent's on/off pref.
    const unsubLocation = onLocationChange(() => player.ensurePlaying());

    const unsub = onSpeechEvent((evt) => {
      if (evt === 'start') player.duck();
      else player.restore();
    });

    // Listen for cross-module theme requests (e.g. from ArcadeLobbyPage in web-app).
    const onThemeEvent = (e: Event) => {
      const theme = (e as CustomEvent<string | null>).detail;
      if (theme) {
        player.setTheme(theme as MusicTheme);
      } else {
        player.setTheme(readMusicPrefs().theme);
      }
      // A theme request means a surface WANTS music sounding (e.g. the arcade
      // lobby). If the mount-time autoplay was blocked and no gesture retried it
      // yet, setTheme alone leaves the element paused → "games have no music".
      // ensurePlaying resumes it (no-op while already playing / blocked).
      player.ensurePlaying();
    };
    window.addEventListener('lwa:music:settheme', onThemeEvent);

    // Re-apply prefs when parent settings page saves changes.
    const onPrefChange = () => {
      const p = readMusicPrefs();
      player.setVolume(p.volume);
      player.setMuted(!p.enabled);
      setEnabledState(p.enabled);
      setVolumeState(p.volume);
      // Disabling music via any surface clears the durable intent so a later cold
      // load doesn't resume against the user's choice (mirrors setEnabled).
      if (!p.enabled) setMusicIntent(false);
    };
    window.addEventListener('lwa:music:prefchange', onPrefChange);

    return () => {
      unsub();
      unsubUnlock();
      unsubLocation();
      window.removeEventListener('lwa:music:settheme', onThemeEvent);
      window.removeEventListener('lwa:music:prefchange', onPrefChange);
      // dispose (not just stop): also detaches the player's document/app
      // listeners so a replaced player can't keep resuming itself forever.
      player.dispose();
      playerRef.current = null;
    };
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    playerRef.current?.setMuted(!v);
    persistMusicPrefs({ backgroundMusicEnabled: v }); // survive relaunch / prefchange re-read
    // Respect the user's on/off choice across cold loads: turning music OFF clears
    // the durable intent so a refresh / cross-app nav never resurrects it. Turning
    // it back ON does NOT set intent=true here — that is set only on a REAL play
    // (the 'playing' event) so a muted-but-never-heard bed can't claim intent.
    if (!v) setMusicIntent(false);
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    playerRef.current?.setVolume(v);
    persistMusicPrefs({ backgroundMusicVolume: v });
  }, []);

  const setTheme = useCallback((theme: MusicTheme) => {
    playerRef.current?.setTheme(theme);
  }, []);

  return (
    <BackgroundMusicContext.Provider value={{ enabled, volume, setEnabled, setVolume, setTheme }}>
      {children}
    </BackgroundMusicContext.Provider>
  );
}
